import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchPaymentDetail, refreshPayment, refundPayment } from "../../api/PaymentAPIs";
import { useAuth } from "../../utils/auth";
import { isAdmin } from "../../utils/roles";

export default function PaymentDetailsPage() {
  const { id } = useParams();
  const { authState } = useAuth();
  const queryClient = useQueryClient();
  const [warning, setWarning] = useState("");
  const [message, setMessage] = useState("");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ["paymentDetail", id],
    queryFn: () => fetchPaymentDetail(id),
  });

  const refresh = async () => {
    setBusy(true);
    setWarning("");
    try {
      const result = await refreshPayment(id);
      setWarning(result.warning || "");
      queryClient.invalidateQueries({ queryKey: ["paymentDetail", id] });
    } catch (err) {
      setWarning(err.message);
    } finally {
      setBusy(false);
    }
  };

  const refund = async () => {
    if (!confirm) {
      setMessage("أكد الاسترداد أولاً.");
      return;
    }
    setBusy(true);
    setMessage("");
    try {
      const result = await refundPayment(id, {
        confirm: true,
        amount: amount ? Number(amount) : null,
        reason,
      });
      setMessage(result.message);
      setConfirm(false);
      queryClient.invalidateQueries({ queryKey: ["paymentDetail", id] });
    } catch (err) {
      setMessage(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (isLoading) return <p>جاري التحميل...</p>;
  if (error) return <p className="text-red-600">{error.message}</p>;
  if (!data) return null;

  return (
    <div className="space-y-4">
      {warning && <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-3">{warning}</div>}
      <div className="grid md:grid-cols-2 gap-4">
        <section className="bg-white border border-slate-200 rounded-2xl p-4 space-y-2">
          <h2 className="font-bold">سجل جراباتكم</h2>
          <Row label="رقم الدفع" value={data.paymentId} />
          <Row label="رقم الطلب" value={data.orderId} />
          <Row label="حالة الطلب" value={data.orderStatus} />
          <Row label="العميل" value={data.customerName} />
          <Row label="المبلغ" value={`${data.amount} ${data.currency}`} />
          <Row label="المسترد" value={data.refundedAmount} />
          <Row label="الحالة المحلية" value={data.status} />
          <Row label="تاريخ الإنشاء" value={format(data.createdAt)} />
          <Row label="تاريخ الدفع" value={format(data.paidAt)} />
          <Row label="سبب الفشل" value={data.failureReason} />
        </section>
        <section className="bg-white border border-slate-200 rounded-2xl p-4 space-y-2">
          <h2 className="font-bold">بيانات Kashier</h2>
          <Row label="المزود" value={data.provider} />
          <Row label="حالة المزود" value={data.providerStatus} />
          <Row label="طريقة الدفع" value={data.paymentMethod} />
          <Row label="الطرق المطلوبة" value={data.requestedMethods} />
          <Row label="رقم العملية" value={data.transactionId} />
          <Row label="رقم طلب Kashier" value={data.providerOrderId} />
          <Row label="رقم الجلسة" value={data.providerSessionId} />
          <Row label="مرجع التاجر" value={data.merchantOrderReference} />
          <Row label="البطاقة" value={[data.cardBrand, data.maskedCard].filter(Boolean).join(" ") || "—"} />
          <Row label="آخر مطابقة" value={format(data.lastReconciledAt)} />
        </section>
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" disabled={busy} onClick={refresh} className="px-4 py-2 rounded-lg bg-emerald-600 text-white disabled:opacity-50">تحديث من Kashier</button>
      </div>
      {isAdmin(authState.role) && (data.status === "Paid" || data.status === "PartiallyRefunded") && (
        <section className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3">
          <h2 className="font-bold">استرداد</h2>
          <p className="text-sm text-slate-500">اترك المبلغ فارغاً لاسترداد المتبقي بالكامل. المبلغ الحالي {data.amount} {data.currency}، المسترد {data.refundedAmount}.</p>
          <input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="مبلغ جزئي اختياري" className="border rounded-lg px-3 py-2 w-full" />
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="سبب الاسترداد" className="border rounded-lg px-3 py-2 w-full" />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} />
            أؤكد استرداد هذا المبلغ
          </label>
          <button type="button" disabled={busy || !confirm} onClick={refund} className="px-4 py-2 rounded-lg bg-red-600 text-white disabled:opacity-50">تنفيذ الاسترداد</button>
          {message && <p>{message}</p>}
        </section>
      )}
      <section className="bg-white border border-slate-200 rounded-2xl p-4">
        <h2 className="font-bold mb-3">سجل الأحداث</h2>
        <div className="space-y-2">
          {(data.events || []).map((event) => (
            <div key={event.paymentEventId} className="border-b border-slate-100 pb-2">
              <p className="font-medium">{event.eventType} {event.providerEvent ? `· ${event.providerEvent}` : ""} {event.providerStatus ? `· ${event.providerStatus}` : ""}</p>
              <p className="text-sm text-slate-500">{event.message} · {format(event.createdAt)}</p>
            </div>
          ))}
        </div>
      </section>
      {data.providerMetadata && (
        <section className="bg-white border border-slate-200 rounded-2xl p-4">
          <h2 className="font-bold mb-2">رد المزود بعد التنقية</h2>
          <pre className="text-xs overflow-x-auto whitespace-pre-wrap">{data.providerMetadata}</pre>
        </section>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-3 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-left">{value || "—"}</span>
    </div>
  );
}

function format(value) {
  return value ? new Date(value).toLocaleString() : "—";
}
